import React, { useState } from 'react';
import { Card, CardMedia, Box, IconButton, Tooltip } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import LoadingDots from '../LoadingDots/LoadingDots';
import { getScaledLogoWidth } from '../../utils/imageUtils';

const PhotoCard = ({ item, isSelected, onSelect, onShare, onPreview, brandedContentSettings, brandedContentImage }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const handleImageLoad = (event) => {
    setIsLoading(false);
    // Capture the natural dimensions of the loaded image
    const { naturalWidth, naturalHeight } = event.target;
    setImageDimensions({ width: naturalWidth, height: naturalHeight });
  };

  const handleImageError = () => {
    setIsLoading(false);
    setError(true);
  };

  const handleCardClick = (e) => {
    // Don't trigger preview if clicking on the share button
    if (e.target.closest('[data-share-button]')) {
      return;
    }
    if (onPreview) {
      onPreview(item);
    }
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    if (onShare) {
      onShare(item);
    }
  };

  return (
    <Card 
      sx={{ 
        position: 'relative',
        cursor: 'pointer',
        transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
        transition: 'transform 0.3s ease-in-out',
        border: isSelected ? '3px solid #db4481' : '3px solid transparent',
        padding: '10px',
        borderRadius: '10px',
        '&:hover': {
          boxShadow: 3,
        },
      }}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box sx={{ 
        position: 'relative', 
        width: '100%',
        // iOS 12 Safari does not support aspect-ratio reliably.
        // Use a padding-top square so image cards keep proper proportions.
        '&::before': {
          content: '""',
          display: 'block',
          paddingTop: '100%'
        }
      }}>
        {/* Share button in upper right corner */}
        <Box sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          opacity: isHovered ? 1 : 0.7,
          transition: 'opacity 0.2s ease-in-out'
        }}>
          <Tooltip title="Add to email">
            <IconButton
              data-share-button
              onClick={handleShareClick}
              size="large"
              sx={{
                width: 58,
                height: 58,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              <SendIcon sx={{ fontSize: '1.5rem' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'transparent',
            }}
          >
            <LoadingDots />
          </Box>
        )}
        <CardMedia
          component="img"
          image={item.url}
          alt={item.key.split('/').pop()}
          sx={{
            objectFit: 'cover',
            opacity: error ? 0.5 : 1,
            visibility: isLoading ? 'hidden' : 'visible',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
        
        {/* Branded Logo Overlay */}
        {brandedContentImage && brandedContentSettings && imageDimensions.width > 0 && (() => {
          // Use utility function to calculate proportional logo size
          const photoCardWidth = 200; // Container width
          const scaledLogoWidth = getScaledLogoWidth(brandedContentSettings, imageDimensions.width, photoCardWidth, 20);
          
          return (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              <Box
                component="img"
                src={brandedContentImage}
                alt="Brand logo overlay"
                sx={{
                  position: 'absolute',
                  width: `${scaledLogoWidth}px`,
                  height: 'auto',
                  maxWidth: '25%', // Limit to 25% of PhotoCard width
                  opacity: 0.7,
                  ...(brandedContentSettings.position === 'upper-left' && {
                    top: '10px',
                    left: '10px',
                  }),
                  ...(brandedContentSettings.position === 'upper-right' && {
                    top: '10px',
                    right: '10px',
                  }),
                  ...(brandedContentSettings.position === 'lower-left' && {
                    bottom: '10px',
                    left: '10px',
                  }),
                  ...(brandedContentSettings.position === 'lower-right' && {
                    bottom: '10px',
                    right: '10px',
                  }),
                }}
              />
            </Box>
          );
        })()}
      </Box>
      {/* <CardContent sx={{ p: 1 }}>
        <Typography variant="body2" noWrap>
          {item.key.split('/').pop()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(item.lastModified).toLocaleString()}
        </Typography>
      </CardContent> */}
    </Card>
  );
};

export default PhotoCard; 